import React, { useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button, CircularProgress, Stack } from "@mui/joy";
import DefaultPage from "pages/container/DefaultPage";
import { useConfirmTossPayments } from "hooks/useConfirmTossPayments";
import useGetEventFromApp from "hooks/useGetEventFromApp";
import useIsInappWebview from "hooks/useIsInappWebview";
import { sendMessage } from "utils/webview";
import { useRecoilState } from "recoil";
import { userAtom } from "../../recoil/userAtom";
import { trackCapiStandardEvent } from "../../analytics/metaCapi";
import { isAdTrackingSuppressed } from "../../analytics/adTracking";
import { getPromotionBySlug } from "../../components/task/payment/planTypes";

const hasPaymentSuccessMessage = (message: any) => {
  if (Array.isArray(message)) {
    return message.some(hasPaymentSuccessMessage);
  }

  return message?.event === "payment" && message?.params?.status === "success";
};

export default function PartnerTossPaymentsSuccess() {
  const [searchParams] = useSearchParams();
  const { slug } = useParams();
  const navigate = useNavigate();
  const isInappWebview = useIsInappWebview();
  const parsedMessageFromApp = useGetEventFromApp();
  const [user, setUser] = useRecoilState(userAtom);
  const shouldSendCloseAllRef = useRef(false);

  const {
    mutate: confirmPayments,
    isLoading,
    isSuccess,
    isError,
    error,
  } = useConfirmTossPayments();

  const errorMessage =
    (error && typeof error === "object" && "message" in error
      ? (error as any).message
      : undefined) || undefined;

  const sendTossPaymentsMessage = (params: Record<string, any>) => {
    shouldSendCloseAllRef.current = params?.status === "success";

    sendMessage(
      JSON.stringify({
        event: "toss_payments",
        params,
      })
    );
  };

  useEffect(() => {
    if (!isInappWebview) return;
    if (!shouldSendCloseAllRef.current) return;
    if (!hasPaymentSuccessMessage(parsedMessageFromApp)) return;

    shouldSendCloseAllRef.current = false;

    setTimeout(() => {
      sendMessage(
        JSON.stringify({
          event: "close_all",
        })
      );
    }, 2000);
  }, [isInappWebview, parsedMessageFromApp]);

  // 결제 확인은 성공 페이지 진입 시 1회만 호출합니다.
  useEffect(() => {
    const orderId = searchParams.get("orderId");
    const paymentKey = searchParams.get("paymentKey");
    const payAmount = Number(searchParams.get("amount"));

    if (orderId && paymentKey && payAmount) {
      // 결제 확인 API 호출 — 파트너 경로는 slug 코드를 promotionCode 로 함께 전달 (백엔드 혜택 식별용)
      confirmPayments(
        { orderId, paymentKey, payAmount, promotionCode: slug },
        {
          onSuccess: () => {
            // 결제 성공 시, 결제 전까지 불변이어야 했던 로컬스토리지 키 초기화
            try {
              // 결제 성공 후 재진입 시 결제 페이지의 page view 관련 이벤트(meta, firebase, appsflyer, mixpanel) 전송을 1회 차단
              localStorage.setItem("suppress_payment_page_view", "1");
            } catch (e) {}

            // 파트너 경로(slug 매핑 존재) 결제는 계정 user.promotion 유무와 무관하게 광고 전송 차단
            const adSuppressed =
              isAdTrackingSuppressed(user) || !!getPromotionBySlug(slug);

            if (!adSuppressed) {
              trackCapiStandardEvent(
                "Purchase",
                {
                  value: payAmount,
                  currency: "KRW",
                  order_id: orderId,
                  externalId: user?.userId,
                  email: user?.email,
                },
                {
                  eventId: orderId ?? undefined,
                },
                undefined,
                true
              );
            }

            if (isInappWebview) {
              if (adSuppressed) {
                // 인사이더/제휴 프로모션: 광고 로그 없이 성공 신호만 전송
                sendTossPaymentsMessage({
                  status: "success",
                });
              } else {
                sendTossPaymentsMessage({
                  status: "success",
                  logEventList: [
                    {
                      eventName: "program_start",
                      value: payAmount,
                      currency: "KRW",
                    },
                    {
                      eventName: "debug_start",
                      value: payAmount,
                      currency: "KRW",
                      byOs: true,
                    },
                  ],
                  firebaseAnalyticsEventList: [
                    {
                      eventName: "purchase",
                      eventParams: {
                        transaction_id: orderId,
                        value: payAmount,
                        currency: "KRW",
                      },
                    },
                  ],
                  appsflyerEventList: [
                    {
                      eventName: "af_program_start",
                      eventParams: {
                        af_content_id: "distancing_program",
                        af_order_id: orderId,
                        af_currency: "KRW",
                        af_revenue: payAmount,
                      },
                    },
                  ],
                });
              }
            } else {
              // 결제 완료 → 홈/프로그램에서 온보딩 강제 진입 방지
              setUser((prev) =>
                prev ? { ...prev, isOnboardingRequired: false } : prev
              );
              // 닉네임 설정/변경 필요 시 닉네임 제출 페이지 → 그 다음 홈
              navigate(
                user?.isNicknameChangeRequired && slug
                  ? `/partner/${slug}/nickname-submit`
                  : "/",
                { replace: true }
              );
            }
          },
        }
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 로딩/성공/실패 UI 렌더
  if (isLoading) {
    return (
      <DefaultPage
        content={
          <Stack alignItems="center">
            <CircularProgress color="neutral" />
          </Stack>
        }
      />
    );
  }

  if (isError) {
    return (
      <DefaultPage
        content={
          <Stack alignItems="center" spacing={1.5}>
            <div>결제 처리 중 오류가 발생했습니다.</div>
            {errorMessage && <div>{errorMessage}</div>}
            <Button
              color="primary"
              onClick={() => {
                if (isInappWebview) {
                  sendMessage(
                    JSON.stringify({
                      event: "toss_payments",
                      params: {
                        status: "cancel",
                      },
                    })
                  );
                } else {
                  navigate(-1);
                }
              }}
            >
              확인
            </Button>
          </Stack>
        }
      />
    );
  }

  if (isSuccess) {
    return (
      <DefaultPage
        content={
          <Stack alignItems="center">
            <CircularProgress color="neutral" />
          </Stack>
        }
      />
    );
  }

  return null;
}
